import { configuration } from '../../../../app/common-reducers.js';
import { disableFacet, enableFacet, updateFacetOptions, } from '../../../../features/facet-options/facet-options-actions.js';
import { isFacetEnabledSelector } from '../../../../features/facet-options/facet-options-selectors.js';
import { facetOptionsReducer as facetOptions } from '../../../../features/facet-options/facet-options-slice.js';
import { deselectAllCategoryFacetValues, registerCategoryFacet, toggleSelectCategoryFacetValue, updateCategoryFacetNumberOfValues, updateCategoryFacetSortCriterion, } from '../../../../features/facets/category-facet-set/category-facet-set-actions.js';
import { categoryFacetResponseSelector } from '../../../../features/facets/category-facet-set/category-facet-set-selectors.js';
import { categoryFacetRequestSelector } from '../../../../features/facets/category-facet-set/category-facet-set-selectors.js';
import { defaultCategoryFacetOptions } from '../../../../features/facets/category-facet-set/category-facet-set-slice.js';
import { categoryFacetSetReducer as categoryFacetSet } from '../../../../features/facets/category-facet-set/category-facet-set-slice.js';
import { findActiveValueAncestry, partitionIntoParentsAndValues, } from '../../../../features/facets/category-facet-set/category-facet-utils.js';
import { categoryFacetSearchSetReducer as categoryFacetSearchSet } from '../../../../features/facets/facet-search-set/category/category-facet-search-set-slice.js';
import { defaultFacetSearchOptions } from '../../../../features/facets/facet-search-set/facet-search-reducer-helpers.js';
import { isFacetLoadingResponseSelector } from '../../../../features/facets/facet-set/facet-set-selectors.js';
import { searchReducer as search } from '../../../../features/search/search-slice.js';
import { selectActiveTab } from '../../../../features/tab-set/tab-set-selectors.js';
import { loadReducerError } from '../../../../utils/errors.js';
import { omit } from '../../../../utils/utils.js';
import { validateOptions } from '../../../../utils/validate-payload.js';
import { buildController, } from '../../../controller/headless-controller.js';
import { determineFacetId } from '../_common/facet-id-determinor.js';
import { categoryFacetOptionsSchema, } from './headless-core-category-facet-options.js';
/**
 * Creates a `CategoryFacet` controller instance.
 *
 * @param engine - The headless engine.
 * @param props - The configurable `CategoryFacet` properties.
 * @returns A `CategoryFacet` controller instance.
 *
 * @group Controllers
 * @category CategoryFacet
 * */
export function buildCoreCategoryFacet(engine, props) {
    if (!loadCategoryFacetReducers(engine)) {
        throw loadReducerError;
    }
    const controller = buildController(engine);
    const { dispatch } = engine;
    const facetId = determineFacetId(engine, props.options);
    const tabs = props.options.tabs ?? {};
    const activeTab = selectActiveTab(engine.state.tabSet);
    const registrationOptions = {
        ...defaultCategoryFacetOptions,
        ...omit('facetSearch', props.options),
        field: props.options.field,
        facetId,
        tabs,
        activeTab,
    };
    const options = {
        facetSearch: { ...defaultFacetSearchOptions, ...props.options.facetSearch },
        ...registrationOptions,
    };
    validateOptions(engine, categoryFacetOptionsSchema, options, 'buildCategoryFacet');
    const getRequest = () => {
        return categoryFacetRequestSelector(engine.state, facetId);
    };
    const getResponse = () => {
        return categoryFacetResponseSelector(engine.state, facetId);
    };
    const getIsLoading = () => isFacetLoadingResponseSelector(engine.state);
    const getIsEnabled = () => isFacetEnabledSelector(engine.state, facetId);
    dispatch(registerCategoryFacet(registrationOptions));
    return {
        ...controller,
        toggleSelect(selection) {
            const retrieveCount = options.numberOfValues;
            dispatch(toggleSelectCategoryFacetValue({ facetId, selection, retrieveCount }));
            dispatch(updateFacetOptions());
        },
        deselectAll() {
            dispatch(deselectAllCategoryFacetValues(facetId));
            dispatch(updateFacetOptions());
        },
        sortBy(criterion) {
            dispatch(updateCategoryFacetSortCriterion({ facetId, criterion }));
            dispatch(updateFacetOptions());
        },
        isSortedBy(criterion) {
            const request = getRequest();
            return request.sortCriteria === criterion;
        },
        showMoreValues() {
            const { numberOfValues: increment } = options;
            const { activeValue, valuesAsTrees } = this.state;
            const numberOfValues = (activeValue?.children.length ?? valuesAsTrees.length) + increment;
            dispatch(updateCategoryFacetNumberOfValues({ facetId, numberOfValues }));
            dispatch(updateFacetOptions());
        },
        showLessValues() {
            const { numberOfValues } = options;
            dispatch(updateCategoryFacetNumberOfValues({ facetId, numberOfValues }));
            dispatch(updateFacetOptions());
        },
        enable() {
            dispatch(enableFacet(facetId));
        },
        disable() {
            dispatch(disableFacet(facetId));
        },
        get state() {
            const request = getRequest();
            const response = getResponse();
            const isLoading = getIsLoading();
            const enabled = getIsEnabled();
            const valuesAsTrees = response?.values ?? [];
            const isHierarchical = valuesAsTrees.some((value) => value.children.length > 0) ?? false;
            const { parents, values } = partitionIntoParentsAndValues(response?.values);
            const selectedValueAncestry = findActiveValueAncestry(valuesAsTrees);
            const activeValue = selectedValueAncestry.length
                ? selectedValueAncestry[selectedValueAncestry.length - 1]
                : undefined;
            const hasActiveValues = !!activeValue;
            const canShowMoreValues = activeValue?.moreValuesAvailable ??
                response?.moreValuesAvailable ??
                false;
            const canShowLessValues = activeValue
                ? activeValue.children.length > options.numberOfValues
                : valuesAsTrees.length > options.numberOfValues;
            return {
                facetId,
                parents,
                selectedValueAncestry,
                values,
                isHierarchical,
                valuesAsTrees,
                activeValue,
                isLoading,
                hasActiveValues,
                canShowMoreValues,
                canShowLessValues,
                sortCriteria: request.sortCriteria,
                enabled,
            };
        },
    };
}
function loadCategoryFacetReducers(engine) {
    engine.addReducers({
        categoryFacetSet,
        categoryFacetSearchSet,
        facetOptions,
        configuration,
        search,
    });
    return true;
}
